/*
 * @FilePath: /mall-fe/src/views/web/product/conponents/ProductAttrList/index.tsx
 * @Description: 商品速采清单
 */
import React, {
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { debounce, cloneDeep } from 'lodash';
import { addCartGoodsList } from 'api/user';
import { addGoodsAnimate } from 'libs/animate';
import useUrl from 'hooks/useUrl';
import { useRouter } from 'next/router';
import styles from './index.module.scss';
import { UserContext, CartContext } from 'libs/contexts';
import { formatCurrency } from '../../helper';
import {
  Pagination,
  Input,
  InputNumber,
  Table,
  Button,
  Tooltip,
  Select,
  Modal,
  message,
} from 'antd';
import type {
  IAttrObj,
  IAttr,
  IProductDetail,
  OrderParams,
  ClickRecommendProduct,
} from '../../product';
import { PAGE_SIZE_OPT } from '../../constants';
import { ATTR_VALUE_TYPE, MMU_DATA_TYPE, QUOTE_MODE } from 'libs/cons';
import { CurrentUserTypeSource } from 'libs/enums';
import { InfoCircleOutlined } from '@ant-design/icons';
import ProductService, { ISkuParams } from 'api/product';
import {
  useTradeModeContext,
  useTradeAgreementContext,
  useNetPriceTypeContext,
  useShopDataContext,
  useProductDetailContext,
} from 'views/web/product/context';
import useAreaCookie from 'hooks/useAreaCookie';
import useDetailPriceAndAgreement from '../../Detail/useDetailPriceAndAgreement';
import useToLogin from 'hooks/useToLogin';
import { checkMmu, formatNumber } from '../helper';
import ProductDetailPriceDescModal from '../ProductDetailPriceDescModal';
import InputNumberComp from 'comps/InputNumber';
import { numberAdd } from 'libs/compute';
import CreateOrder from '../CreateOrder';
interface IProps {
  autoLoad?: boolean; // 是否自动加载,判断是「速采清单」 or 「推荐清单」
  currentSelectedRecommendProduct?: ClickRecommendProduct; // 当前选中的推荐商品
  attributeList: any[];
  mmuAttributeList: any[];
  skuDetail: IProductDetail;
}

type IItemProps = {
  disabled: boolean;
  attribute: {
    categoryAttributeValueList: IAttr[];
    hasCustomize: boolean;
    modeType: number;
    dataType: number;
  };
};

// 商品规格列表查询参数
const listParams = {
  pageNo: 1,
  pageSize: 10,
  model: '', // 查询参数
};

const resetListParams = () => {
  listParams.pageNo = 1;
  listParams.model = '';
  listParams.pageSize = 10;
};

export const InputSelectItem = (props: IItemProps) => {
  const {
    disabled,
    attribute: { hasCustomize, modeType, dataType, categoryAttributeValueList },
  } = props;
  const isRadio = () => !hasCustomize && modeType === ATTR_VALUE_TYPE.RADIO.value;
  const isText = () => hasCustomize && dataType === MMU_DATA_TYPE.TEXT.value;
  const isFloat = () => hasCustomize && dataType === MMU_DATA_TYPE.FLOAT.value;
  const isInt = () => hasCustomize && dataType === MMU_DATA_TYPE.INT.value;

  const getDefaultValue = () => {
    const defaultAttr = categoryAttributeValueList.find((item) => item.active);
    if (isRadio()) return defaultAttr?.attributeValueSysNo || '';
    return defaultAttr?.attributeValue || 0;
  };
  const onSelected = (sysNo: string) => {
    props.attribute.categoryAttributeValueList.forEach((i) => {
      if (sysNo === i.attributeValueSysNo) {
        i.active = true;
      } else {
        i.active = false;
      }
    });
  };
  const onChange = (value: string) => {
    if (categoryAttributeValueList) {
      props.attribute.categoryAttributeValueList[0].attributeValue = value;
      props.attribute.categoryAttributeValueList[0].active = true;
    }
  };
  return (
    <>
      {isRadio() && (
        <div>
          <Select
            defaultValue={getDefaultValue()}
            style={{ width: 100 }}
            onChange={(sysNo) => onSelected(sysNo)}
            disabled={disabled}
          >
            {categoryAttributeValueList?.map((item) => (
              <Select.Option value={item.attributeValueSysNo} key={item.attributeValueSysNo}>
                {item.attributeValue}
              </Select.Option>
            ))}
          </Select>
        </div>
      )}
      {isInt() && (
        <div>
          <InputNumber
            defaultValue={Number(getDefaultValue())}
            onChange={(value) => onChange(String(value))}
            min={0}
            max={99999999}
            step={1}
            disabled={disabled}
          />
        </div>
      )}
      {(isText() || isFloat()) && (
        <div>
          <Input
            defaultValue={getDefaultValue()}
            onChange={(e) => onChange(e?.target.value)}
            disabled={disabled}
            prefix={categoryAttributeValueList[0]?.prefix}
            suffix={categoryAttributeValueList[0]?.suffix}
          />
        </div>
      )}
    </>
  );
};
const COLUMN_MIN_WIDTH = 200;
export default forwardRef(function ProductAttrListComp(props: IProps, ref) {
  const { mmuAttributeList, skuDetail, autoLoad, currentSelectedRecommendProduct } = props;
  // 交易方式
  const { selectTradeMode } = useTradeModeContext();
  // 价格类型
  const { selectNetPriceType } = useNetPriceTypeContext();
  // 交易协议
  const { selectTradeAgreement } = useTradeAgreementContext();
  // 是否显示价格
  const { isThePriceDisplayed } = useDetailPriceAndAgreement(skuDetail);
  const { shop } = useShopDataContext();
  const { setShowLoading } = useProductDetailContext();
  const route = useRouter();
  const preview = route?.query?.preview;
  const userInfo = useContext(UserContext) || ({} as UserInfo);
  // 表格渲染数据
  const [attrListObj, setAttrListObj] = useState({ pageNo: 1, pageSize: 10, total: 0 } as IAttrObj);
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const { getCookieAreaSysNo } = useAreaCookie();
  const { getPublicUrl } = useUrl();
  const { handleLogin } = useToLogin();
  const { refreshData } = useContext(CartContext);
  const quoteMode = (attrListObj?.mallSkuList && attrListObj?.mallSkuList[0]) || {};
  const hasAgreement = selectTradeAgreement?.name;
  const [skuForPriceCalculate, setSkuForPriceCalculate] = useState({});
  // 用于存储copy data
  const [copiedRows, setCopiedRows] = useState<{ [key: string]: any }>({});

  // 引用批量下单组件
  const createOrderRef = useRef<{ excuteOrder: (orderParams: OrderParams) => void }>(null);
  const priceWithOutTaxTip = () => {
    if (quoteMode === QUOTE_MODE.WITHOUT_TAX_TYPE.value) {
      return !!hasAgreement ? '系统自动计算' : '';
    } else {
      return '除税单价=含税单价 /（1+税率）';
    }
  };
  const priceWithTaxTip = () => {
    if (quoteMode !== QUOTE_MODE.WITHOUT_TAX_TYPE.value) {
      return !!hasAgreement ? '系统自动计算' : '';
    } else {
      return '含税单价=除税单价*（1+税率）';
    }
  };
  const skuSaleStatus = (sku) => {
    //  3 : "尝试获取协议基价，协议基价缺失",
    if (sku?.skuForPriceCalculate?.agreementPriceMissingReason === 3 && sku.salesPrice !== null) {
      return { canSale: false, tip: '暂无协议价' };
    }
    const isContainSelectedAgre = (sku.agreementList || []).some(
      ({ sysNo }) => sysNo === selectTradeAgreement?.sysNo,
    );
    const canSale = sku.agreementCanSale !== false && sku.areaCanSale && isContainSelectedAgre;
    let tip;
    if (!canSale) {
      tip = !sku.areaCanSale
        ? '此商品不在此地区范围内销售'
        : sku.inventory === 0
        ? '无货，此商品暂时售完'
        : '暂不支持该交易规则';
    }
    return { tip, canSale: canSale && sku.inventory !== 0 };
  };

  const getProductSkuList = async (param: any = {}) => {
    if (!skuDetail.useSkuList) {
      const categoryAttributeList = mmuAttributeList?.find(
        (item) => item.attributeName === '卸车',
      )?.categoryAttributeValueList;
      // 商品详情查询参数
      const params = {
        ...listParams,
        mallSkuSysNo: skuDetail.sysNo,
        agreementSysNo: selectTradeAgreement.sysNo,
        accountPeriod: selectTradeMode.accountPeriod,
        billingPeriod: selectTradeMode.billingPeriod,
        areaSysNo: getCookieAreaSysNo(),
        supplierSysNo: skuDetail.supplierSysNo,
        mallSkuList: attrListObj?.mallSkuList, //传入上一次的 mallSkuList（因为用户可能手动输入）
        categoryAttributeList,
        netPriceType: selectNetPriceType,
        ...param,
      } as ISkuParams;
      const { data } = await ProductService.getProductSkuList(params);
      setAttrListObj(data);
    }
  };

  // attrListObj.mallSkuList 是表格的数据源 datasource
  // 「推荐清单」里获取，清单的接口
  const getProductSkuListBySelectedRecommendProduct = async (
    selectedRecommendProduct: ClickRecommendProduct,
  ) => {
    // useSkuList 是否使用清单，「混凝土」该值是「true」，「非混凝土」该值是「false」
    const { useSkuList } = selectedRecommendProduct;
    if (!useSkuList) {
      const categoryAttributeList = selectedRecommendProduct.mmuAttributeList?.find(
        (item) => item.attributeName === '卸车',
      )?.categoryAttributeValueList;
      // 商品详情查询参数
      const params = {
        ...listParams,
        mallSkuSysNo: selectedRecommendProduct.bestSkuSysNo,
        agreementSysNo: selectTradeAgreement.sysNo,
        accountPeriod: selectTradeMode.accountPeriod,
        billingPeriod: selectTradeMode.billingPeriod,
        areaSysNo: getCookieAreaSysNo(),
        supplierSysNo: selectedRecommendProduct.supplierSysNo,
        mallSkuList: attrListObj?.mallSkuList, //传入上一次的 mallSkuList（因为用户可能手动输入）
        categoryAttributeList,
        netPriceType: selectNetPriceType,
        isRecommend: true,
      } as ISkuParams;
      const { data } = await ProductService.getProductSkuList(params);
      setAttrListObj(data);
    }
  };

  const fetchTableData = autoLoad
    ? getProductSkuListBySelectedRecommendProduct.bind(null, currentSelectedRecommendProduct)
    : getProductSkuList;

  const optimizedPurchaser = () => {
    // mall-ui注入值 modelAndView.addObject("optimizedPurchaser", CurrentAuthUser.getCurrent() == null || CurrentAuthUser.isPurchaser()
    // || USER_TYPE == CurrentAuthUser.getCurrent().getCurrentUserType());
    return (
      !userInfo.sysNo ||
      userInfo.isPurchaser ||
      userInfo.currentUserType === CurrentUserTypeSource.personal
    );
  };
  const getUnit = () => {
    const [firstSku] = attrListObj?.mallSkuList || [];
    return firstSku?.unitName;
  };
  const isCompShow = () => {
    if (preview || !optimizedPurchaser) return false;
    // 如果查询条件 model为空   spuForAllSkuList 有值展示，无值不展示
    if (listParams.model) return true;
    return !!attrListObj?.mallSkuList?.length;
  };

  const wrappedColumns = () => {
    // 「推荐清单」与当前商品无关，渲染内容以  attrListObj
    const dataSource = attrListObj?.mallSkuList || [];
    const finalAttributeList = autoLoad ? dataSource[0]?.attributeList : props?.attributeList;
    const finalMmuAttributeList = autoLoad
      ? dataSource[0]?.mmuAttributeList
      : props?.mmuAttributeList;

    const totalColumnLen = (finalAttributeList?.length || 0) + (finalMmuAttributeList?.length || 0);
    let width = 710 / (totalColumnLen + 1);
    let firstWidth = COLUMN_MIN_WIDTH;
    if (width < COLUMN_MIN_WIDTH) {
      width = COLUMN_MIN_WIDTH;
      firstWidth = COLUMN_MIN_WIDTH;
    } else {
      firstWidth = 710 - width * totalColumnLen;
    }

    const prefixArr = [
      {
        title: '商品编号',
        key: 'sysNo',
        dataIndex: 'sysNo',
        width: COLUMN_MIN_WIDTH,
        isPopoverColumn: false,
      },
    ];

    //NOTE: 关键属性
    const attributeArr =
      finalAttributeList?.map((attr, index) => ({
        title: attr.attributeName,
        width: firstWidth,
        render: (record) => {
          const item = record.attributeList.find((v) => v.attributeSysNo === attr.attributeSysNo);
          const value = item?.categoryAttributeValueList?.[0];
          if (!skuSaleStatus(record).canSale && index === 0) {
            return (
              <div>
                <Tooltip
                  title={skuSaleStatus(record).tip}
                  visible={true}
                  autoAdjustOverflow={false}
                  getPopupContainer={(trigger: { parentNode: any }) => trigger.parentNode}
                >
                  {value?.attributeValue}
                </Tooltip>
              </div>
            );
          }
          return value?.attributeValue;
        },
        attributeSysNo: attr.attributeSysNo,
      })) || [];

    //NOTE: 非关键属性
    const mmuAttributeArr =
      finalMmuAttributeList?.map((attr) => {
        return {
          title: attr.attributeName,
          render: (record) => {
            const curMmuAttr = record.mmuAttributeList.find(
              (mmuAttr) => mmuAttr.attributeSysNo === attr.attributeSysNo,
            );
            return (
              <InputSelectItem
                attribute={curMmuAttr}
                key={JSON.stringify(record)}
                disabled={!skuSaleStatus(record).canSale}
              />
            );
          },
          isPopoverColumn: false,
          attributeSysNo: attr.attributeSysNo,
          width: firstWidth,
        };
      }) || [];

    const priceArr = [
      {
        title: '单价(元)',
        key: 'priceWithTax',
        dataIndex: 'priceWithTax',
        isPopoverColumn: false,
        width: firstWidth,

        render: (record, value) => {
          return formatCurrency(value?.priceWithTax);
        },
      },
    ];

    const whowPriceArr = [
      {
        title: () => {
          if (priceWithOutTaxTip()) {
            return (
              <div>
                除税单价(元)
                <Tooltip title={priceWithOutTaxTip}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
            );
          }
          return '除税单价(元)';
        },
        key: 'priceWithOutTax',
        dataIndex: 'priceWithOutTax',
        width: 150,
        // fixed: 'right',
        render: (record, value) => {
          if (value?.priceWithOutTax === null) return '--';
          return (
            <div className={styles['product-col-price']}>
              {formatCurrency(value?.priceWithOutTax)}
            </div>
          );
        },
      },
      {
        title: '税率',
        key: 'taxRate',
        dataIndex: 'taxRate',
        width: 70,
        isPopoverColumn: false,
        // fixed: 'right',
        render: (record, value) => {
          if (value.taxRate) return `${value.taxRate}%`;
          return '--';
        },
      },
      {
        title: () => {
          if (priceWithTaxTip()) {
            return (
              <div>
                含税单价(元)
                <Tooltip title={priceWithTaxTip}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
            );
          }
          return '含税单价(元)';
        },
        key: 'priceWithTax',
        dataIndex: 'priceWithTax',
        width: 140,
        isPopoverColumn: false,
        // fixed: 'right',
        render: (record, value) => {
          if (value?.priceWithTax === null) return '--';
          const showTip =
            hasAgreement &&
            value.quoteMode === QUOTE_MODE.WITH_TAX_TYPE.value &&
            value.skuForPriceCalculate;
          if (!showTip) return formatCurrency(value?.priceWithTax);
          return (
            <span>
              {formatCurrency(value?.priceWithTax)}
              <i
                className={`iconfont icon-jisuanqi ${styles['computed-icon']}`}
                onClick={() => {
                  setShowPriceDialog(true);
                  setSkuForPriceCalculate(value.skuForPriceCalculate);
                }}
              />
            </span>
          );
        },
      },
    ];

    const suffixArr = [
      {
        title: '库存',
        isPopoverColumn: false,
        width: 80,
        fixed: 'right',
        render: (record) => {
          if (!record?.showInventory) return '';
          if (!!record?.inventory) return `${record.inventory}${record.unitName}可售`;
          if (record?.inventory == null) return '有货';
          return '无货，此商品暂时售完';
        },
      },
      {
        title: '数量',
        isPopoverColumn: false,
        width: 180,
        fixed: 'right',
        render: (record) => {
          return (
            <div className={styles.flex}>
              <InputNumberComp
                disabled={!skuSaleStatus(record).canSale}
                // max={record.shopQuantity}
                min={0}
                value={record.shopQuantity || 0}
                onChange={(v) => {
                  record.shopQuantity = v;
                  setAttrListObj({ ...attrListObj });
                }}
              />
              &nbsp;
              {record.unitName}
            </div>
          );
        },
      },
    ];

    const operationColumn = {
      title: '操作',
      align: 'center',
      width: 80,
      fixed: 'right',
      render: (_, record, index) => (
        <Button type="link" onClick={() => handleCopyRow(record, index)}>
          复制
        </Button>
      ),
    };

    if (isThePriceDisplayed.display) {
      return [
        ...prefixArr,
        ...attributeArr,
        ...mmuAttributeArr,
        ...whowPriceArr,
        ...suffixArr,
        operationColumn,
      ];
    }
    return [
      ...prefixArr,
      ...attributeArr,
      ...mmuAttributeArr,
      ...priceArr,
      ...suffixArr,
      operationColumn,
    ];
  };

  const handleRequestPreCheck = () => {
    if (!userInfo) {
      Modal.confirm({
        title: '提示框',
        content: '请先完成登录，再继续操作！',
        okText: '确定',
        cancelText: '取消',
        onOk: function () {
          handleLogin();
        },
      });
      return false;
    }
    return true;
  };

  // 只统计了当前页的商品数量
  const totalShopQuantity = useMemo(() => {
    const VArr = attrListObj?.mallSkuList?.map(({ shopQuantity }) => shopQuantity);
    return (
      VArr?.reduce((acc, curV) => {
        return numberAdd(acc, curV);
      }, 0) || 0
    );
  }, [attrListObj]);

  const totalPrice = useMemo(() => {
    const total = attrListObj?.mallSkuList?.reduce((acc, curSku) => {
      if (skuSaleStatus(curSku).canSale) {
        var total = +formatNumber(+curSku.shopQuantity * curSku.priceWithTax, '0.[00]');
        return acc + total;
      } else {
        return acc;
      }
    }, 0);
    return formatNumber(total, '0.[00]');
  }, [attrListObj]);

  // 批量加入购物车
  const handleBatchAddShoppingCart = async (event) => {
    const postSku = attrListObj?.mallSkuList || []; // 取组件选中商品
    if (totalShopQuantity <= 0) return;
    if (!handleRequestPreCheck()) return;
    const skuList = [];
    postSku?.forEach(function (sku) {
      if (sku.shopQuantity && skuSaleStatus(sku).canSale) {
        skuList.push({
          mallSkuSysNo: sku.sysNo,
          quantity: sku.shopQuantity,
          shopQuantity: sku.shopshopQuantity,
          warehouseSysNo: sku.warehouseSysNo,
          mmuAttributeList: sku.mmuAttributeList,

          billingPeriod: selectTradeMode.billingPeriod,
          accountPeriod: selectTradeMode.accountPeriod,
          agreementSysNo: selectTradeAgreement.sysNo,
          netPriceType: selectNetPriceType,
        });
      }
    });
    const flag = skuList.some((sku) => !checkMmu(sku.mmuAttributeList));
    if (flag) return;
    await addCartGoodsList(skuList);
    addGoodsAnimate(
      event,
      getPublicUrl(props.skuDetail.mainImage),
      document.querySelector('.side-bar-icon'),
    );
    refreshData();
  };

  // 批量下单
  const handleBatchOrder = debounce(
    () => {
      if (!handleRequestPreCheck()) return;

      if (totalShopQuantity <= 0) {
        message.error('请输入采购数量！');
        return false;
      }

      const skuList = [];
      const postSku = attrListObj?.mallSkuList || []; // 取组件选中商品
      postSku?.forEach(function (sku) {
        if (sku.shopQuantity && skuSaleStatus(sku).canSale) {
          skuList.push({
            mallSkuSysNo: sku.sysNo,
            warehouseSysNo: sku.warehouseSysNo,
            quantity: sku.shopQuantity,
            shopQuantity: sku.shopQuantity,
            mmuAttributeList: sku.mmuAttributeList,
            priceFlag: sku.priceFlag,
          });
        }
      });
      const flag = skuList.some((sku) => !checkMmu(sku.mmuAttributeList));
      if (flag) return;

      const orderParams = {
        areaSysNo: getCookieAreaSysNo(),
        type: 0,
        mmuProductEnable: true, // 清单下单，这个值固定true
        simulateSplitGroup: [
          {
            tradeAgreementSysNo: selectTradeAgreement.sysNo,
            shopSysNo: shop.sysNo,
            supplierSysNo: skuDetail.supplierSysNo,
            paymentType: selectTradeMode.accountPeriod,
            billingPeriod: selectTradeMode.billingPeriod,
            netPriceType: selectNetPriceType,
            skuType: skuDetail.skuType,
            skuList,
          },
        ],
      };
      createOrderRef.current?.excuteOrder(orderParams);
      // NOTE: 显示load，页面redirect后不用考虑loading
      setShowLoading(true);
    },
    1000,
    { leading: true, trailing: false },
  );

  // 搜索，重置复制行
  const onSearch = (e) => {
    listParams.model = e;
    listParams.pageNo = 1;

    fetchTableData();
    setCopiedRows({});
  };

  // 复制行
  const handleCopyRow = (record, index) => {
    const copyRecord = cloneDeep(record);
    const newRecord = {
      ...copyRecord,
      sysNoWithCopy: `${record.sysNo}_copy_${Date.now()}`,
      isCopied: true, // 标记这是一个复制的行
    };

    // 更新复制的数据存储
    setCopiedRows((prev) => ({
      ...prev,
      [newRecord.sysNoWithCopy]: newRecord,
    }));

    // 更新当前页面显示，copy 数据不参与分页
    const newList = [...attrListObj.mallSkuList];
    newList.splice(index + 1, 0, newRecord);
    setAttrListObj({
      ...attrListObj,
      mallSkuList: newList,
    });
  };

  // 分页逻辑，考虑当前页的复制数据
  const handlePageChange = async (pageNo, pageSize) => {
    listParams.pageNo = pageNo;
    listParams.pageSize = pageSize;
    // 获取后端数据
    await fetchTableData();

    // 在新数据中插入当前页应该显示的复制数据
    const copiedRowsArray = Object.values(copiedRows);
    if (copiedRowsArray.length > 0) {
      // 找出属于当前页的复制数据
      const copiedRowsInCurrentPage = copiedRowsArray.filter((row) => {
        const [originalSysNo] = row.sysNoWithCopy.split('_copy_');
        const originalRow = attrListObj.mallSkuList.find((item) => item.sysNo === originalSysNo);
        return !!originalRow; // 如果在当前页找到原始行，就显示复制行
      });

      // 将复制的行插入到对应的位置
      const newList = [...attrListObj.mallSkuList];
      copiedRowsInCurrentPage.forEach((copiedRow) => {
        const [originalSysNo] = copiedRow.sysNoWithCopy.split('_copy_');
        const originalIndex = newList.findIndex((item) => item.sysNo === originalSysNo);
        if (originalIndex !== -1) {
          newList.splice(originalIndex + 1, 0, copiedRow);
        }
      });
      //NOTE: 复制的数据不参与分页
      setAttrListObj((prev) => ({
        ...prev,
        mallSkuList: newList,
      }));
    }
  };

  // 「速采清单」场景，命令式调用加载数据
  useImperativeHandle(ref, () => {
    return {
      getProductSkuList,
    };
  });

  // NOTE: 「推荐清单」场景，自动加载
  useEffect(() => {
    if (autoLoad && currentSelectedRecommendProduct) {
      resetListParams();
      getProductSkuListBySelectedRecommendProduct(currentSelectedRecommendProduct);
    }
  }, [autoLoad, currentSelectedRecommendProduct]);

  if (!isCompShow())
    return (
      <div className={styles.empty_container}>{autoLoad ? '暂无推荐清单' : '暂无速采清单'}</div>
    );

  return (
    <div className={styles.attr_container}>
      <div className={styles.search_header}>
        <span className={styles.search_total_tip}>共{attrListObj?.total}条规格</span>
        <Input.Search
          placeholder="请输入商品属性值"
          onSearch={onSearch}
          enterButton="搜索"
          style={{ width: 290 }}
        />
      </div>
      <div className={styles.attr_table}>
        <Table
          columns={wrappedColumns()}
          dataSource={attrListObj?.mallSkuList || []}
          rowKey={(record) => record.sysNoWithCopy || record.sysNo}
          pagination={false}
          scroll={{ x: 1500 }}
        />
      </div>
      <div className={styles.attr_table_footer}>
        <div className={styles.product_table_footer_count}>
          <span className={styles.count_num}>{totalShopQuantity}</span>
          <span className={styles.total_price_prefix}>&nbsp;{getUnit()}</span>
        </div>
        <div className={styles.product_table_footer_price}>
          <span className={styles.total_price_prefix}>合计</span>
          <span className={styles.total_price}>￥ {totalPrice}</span>
        </div>
        <div style={{ display: 'flex' }}>
          <Button
            type="primary"
            onClick={handleBatchAddShoppingCart}
            disabled={totalShopQuantity <= 0}
            style={{ marginRight: '12px' }}
          >
            <i className="iconfont icon-shoppingCart" />
            <span>批量加入购物车</span>
          </Button>
          <Button type="primary" onClick={handleBatchOrder} disabled={totalShopQuantity <= 0}>
            <span>批量下单</span>
          </Button>
        </div>
      </div>
      <div className={styles.attr_pagenation}>
        <Pagination
          showTotal={(total) =>
            `共${Math.ceil(total / attrListObj?.pageSize || 10)}页 / ${total}条记录`
          }
          showQuickJumper
          pageSizeOptions={PAGE_SIZE_OPT}
          current={attrListObj?.pageNo}
          pageSize={attrListObj?.pageSize || 10}
          total={attrListObj?.total}
          onChange={handlePageChange}
        />
      </div>

      <ProductDetailPriceDescModal
        title="报价因素"
        visible={showPriceDialog}
        onClose={() => setShowPriceDialog(false)}
        skuForPriceCalculate={skuForPriceCalculate}
      />

      <CreateOrder ref={createOrderRef} />

      <style jsx>{`
        :global(.ant-tooltip-inner) {
          min-height: 26px;
          padding: 0 12px;
          line-height: 26px;
          font-size: 12px;
        }
        :global(.ant-tooltip) {
          z-index: 9;
        }
        :global(.ant-table-cell) {
          padding: 10px !important;
        }
      `}</style>
    </div>
  );
});
